import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { pageMobileCardSx } from "../../styles/pageModernStyles";
import { expenseUserStatusLabel, ticketAmountLine } from "./expenseUserI18n";
import { getExpenseUserStatusChipProps, KIND_STAFF } from "./expenseUserStatusChips";
import {
  isCompanyCardTicket,
  ticketPaymentChipProps,
  ticketPaymentTypeChipProps,
} from "./expenseUserPaymentChips";

const ExpensesUserTicketsMobile = ({ theme, rows, t, onOpen }) => {
  if (!rows.length) return null;

  return (
    <Stack spacing={1.25}>
      {rows.map((row) => {
        const statusProps = getExpenseUserStatusChipProps(row.Status, row.Kind);
        const payTypeProps = ticketPaymentTypeChipProps(row, t);
        const payProps = ticketPaymentChipProps(row.PaymentStatus, t);
        const accent = theme.palette.primary.main;

        return (
          <Box
            key={row.id}
            onClick={() => onOpen(row.id)}
            sx={{
              ...pageMobileCardSx(theme, { accentColor: accent }),
              pl: 2,
              cursor: "pointer",
              "&:active": { transform: "scale(0.99)" },
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.12),
                  color: accent,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: 0.3 }}
                >
                  #{row.id}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.88rem",
                    lineHeight: 1.25,
                    wordBreak: "break-word",
                  }}
                >
                  {row.OriginalFileName || t("ExpenseUser.note.noFileName")}
                </Typography>
                <Stack direction="row" spacing={0.4} alignItems="center" sx={{ mt: 0.5 }}>
                  <PersonOutlineOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
                    {row.Owner?.username ||
                      row.Owner?.email ||
                      (row.UserId != null
                        ? `#${row.UserId}`
                        : t("ExpenseUser.emptyDash"))}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={
                  row.Kind === KIND_STAFF
                    ? t("ExpenseUser.kindPersonal")
                    : t("ExpenseUser.kindSupplier")
                }
                sx={{ height: 22, fontWeight: 700, fontSize: "0.65rem" }}
              />
              <Chip
                size="small"
                label={expenseUserStatusLabel(t, row.Status)}
                {...statusProps}
                sx={{ height: 22, fontWeight: 700, fontSize: "0.65rem" }}
              />
              <Chip
                size="small"
                {...payTypeProps}
                sx={{ height: 22, fontWeight: 700, fontSize: "0.65rem" }}
              />
              {!isCompanyCardTicket(row) && (
                <Chip
                  size="small"
                  {...payProps}
                  sx={{ height: 22, fontWeight: 700, fontSize: "0.65rem" }}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={ticketAmountLine(t, row)}
                sx={{ height: 22, fontWeight: 800, fontSize: "0.65rem" }}
              />
              {row.A3Reference ? (
                <Chip
                  size="small"
                  color="success"
                  label={`A3: ${row.A3Reference}`}
                  sx={{ height: 22, fontWeight: 700, fontSize: "0.65rem" }}
                />
              ) : null}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default ExpensesUserTicketsMobile;
